import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth/auth'
import { useMe } from '@/lib/api/hooks'
import { Logo } from './shell/Logo'

/** Full-screen splash while the backend onboards the user (clones the demo workspace). */
function OnboardingSplash({ error, onRetry }: { error?: boolean; onRetry?: () => void }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center">
        <Logo className="h-5 w-auto text-foreground" />
        {error ? (
          <>
            <div className="text-sm font-semibold text-ignition">Couldn&rsquo;t load your workspace</div>
            <p className="max-w-xs text-[12.5px] text-muted-foreground">
              We couldn&rsquo;t reach the service or your session has expired.
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="rounded-md border border-input px-3 py-1.5 text-[13px] font-semibold hover:bg-secondary"
              >
                Try again
              </button>
            )}
          </>
        ) : (
          <>
            <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
            <div className="text-sm font-medium text-muted-foreground">Setting up your workspace…</div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Gate the app on a real Entra session AND first-sign-in onboarding. Unauthenticated
 * users go to /login; authenticated users wait on GET /me (which clones the demo
 * workspace on first sign-in) before any site data renders.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const loc = useLocation()
  const me = useMe(isAuthenticated) // hook called unconditionally; disabled when signed out

  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: loc.pathname }} />
  if (me.isError) return <OnboardingSplash error onRetry={() => me.refetch()} />
  if (!me.data) return <OnboardingSplash />
  return <>{children}</>
}
